import { useCallback, useEffect, useRef, useState } from 'react'

import { retryWithBackoff } from './retryWithBackoff'

export interface ConnectivityAdapter {
    isOnline: () => boolean
    subscribe: (onOnline: () => void) => () => void
}

export type ConfigBootstrapStatus = 'loading' | 'ready' | 'error'

export interface UseConfigBootstrapOptions {
    /** Loads config and populates the store. Rejects on failure. */
    load: () => Promise<void>
    connectivity: ConnectivityAdapter
    /** Skip loading entirely (e.g. not authenticated yet, or already initialized). */
    enabled: boolean
    /** Override the default retry schedule — mainly for tests. */
    backoffMs?: number[]
}

export interface ConfigBootstrapResult {
    status: ConfigBootstrapStatus
    error: Error | null
    /** Re-runs the load/retry cycle from scratch. */
    retry: () => void
}

/**
 * Drives a config `load()` through `retryWithBackoff`, exposing React state
 * for the UI: `loading` throughout the retry cycle (existing loaders/spinners
 * keep showing — no flashed error on a single transient hiccup), `error`
 * only once the retry budget is exhausted, `ready` on success.
 */
export function useConfigBootstrap(options: UseConfigBootstrapOptions): ConfigBootstrapResult {
    const { load, connectivity, enabled, backoffMs } = options
    const [status, setStatus] = useState<ConfigBootstrapStatus>('loading')
    const [error, setError] = useState<Error | null>(null)
    const [generation, setGeneration] = useState(0)
    const loadRef = useRef(load)
    loadRef.current = load

    useEffect(() => {
        if (!enabled) {
            return undefined
        }
        let cancelled = false
        setStatus('loading')
        setError(null)

        retryWithBackoff({
            attempt: () => loadRef.current(),
            isOnline: connectivity.isOnline,
            subscribeOnline: connectivity.subscribe,
            backoffMs,
        })
            .then(() => {
                if (!cancelled) {
                    setStatus('ready')
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err : new Error(String(err)))
                    setStatus('error')
                }
            })

        return () => {
            cancelled = true
        }
        // `load` is read via `loadRef`, not a dependency — a caller passing a
        // new function identity every render must not restart the retry cycle.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, generation, connectivity, backoffMs])

    const retry = useCallback(() => setGeneration(g => g + 1), [])

    return { status, error, retry }
}
